import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { getCurrentShop, getCurrentUser } from "@/lib/auth";
import { getTransfer, receiveTransfer } from "@/lib/api";
import { Loader2, Download, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export function ReceiveTransferDialog({
  transferId, show, onClose, onSuccess,
}: {
  transferId: string | null;
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const currentShop = getCurrentShop()!;
  const currentUser = getCurrentUser();
  const [transfer, setTransfer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [receivedQtys, setReceivedQtys] = useState<Record<string, number>>({});

  useEffect(() => {
    if (show && transferId) {
      setLoading(true);
      setNotes("");
      setReceivedQtys({});
      getTransfer(currentShop.id, transferId)
        .then((t) => {
          setTransfer(t);
          const initial: Record<string, number> = {};
          t.items?.forEach((item: any) => {
            initial[item.id] = item.quantitySent;
          });
          setReceivedQtys(initial);
        })
        .catch(() => setTransfer(null))
        .finally(() => setLoading(false));
    } else {
      setTransfer(null);
    }
  }, [show, transferId, currentShop.id]);

  const hasDiscrepancy = Object.entries(receivedQtys).some(
    ([id, qty]) => {
      const item = transfer?.items?.find((i: any) => i.id === id);
      return item && qty !== item.quantitySent;
    },
  );

  const handleReceive = async (withIssue: boolean) => {
    if (!transferId || !transfer) return;
    setIsSaving(true);
    try {
      const items = Object.entries(receivedQtys).map(([id, quantityReceived]) => ({
        id, quantityReceived,
      }));
      await receiveTransfer(currentShop.id, transferId, {
        items,
        notes: withIssue ? (notes || "Recibido con incidencias") : notes,
      });
      toast({ title: withIssue ? "Recepción registrada con incidencias" : "Transferencia recibida exitosamente" });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Confirmar Recepción — {transfer?.sequential || ""}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : !transfer ? (
          <p className="py-8 text-center text-muted-foreground">Transferencia no encontrada</p>
        ) : (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Recibiendo de: <strong>{transfer.shopOrigin?.name || transfer.shopOriginId}</strong>
            </p>

            {/* Product list with editable quantities */}
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-2 text-left">Producto</th>
                    <th className="p-2 text-right">Enviado</th>
                    <th className="p-2 text-right">Recibido *</th>
                    <th className="p-2 text-right">Dif</th>
                  </tr>
                </thead>
                <tbody>
                  {transfer.items?.map((item: any) => {
                    const received = receivedQtys[item.id] ?? item.quantitySent;
                    const diff = received - item.quantitySent;
                    return (
                      <tr key={item.id} className="border-b">
                        <td className="p-2 font-medium">{item.product?.name || "—"}</td>
                        <td className="p-2 text-right">{item.quantitySent}</td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            min={0}
                            max={item.quantitySent}
                            value={received}
                            onChange={(e) => setReceivedQtys((prev) => ({ ...prev, [item.id]: Number(e.target.value) }))}
                            className="w-20 text-right inline-block h-8"
                          />
                        </td>
                        <td className={`p-2 text-right ${diff < 0 ? "text-destructive font-medium" : diff > 0 ? "text-green-600" : ""}`}>
                          {diff !== 0 ? (diff > 0 ? `+${diff}` : diff) : "0"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasDiscrepancy && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Hay diferencias en los productos recibidos</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Se generará automáticamente un movimiento de merma por las cantidades faltantes.
                  </p>
                </div>
              </div>
            )}

            <div>
              <Label>Notas sobre la recepción</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={hasDiscrepancy ? "Describe las incidencias encontradas..." : "Notas opcionales..."}
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button variant="secondary" onClick={() => handleReceive(true)} disabled={isSaving} className={hasDiscrepancy ? "" : "hidden"}>
                {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : <><AlertTriangle className="mr-2 h-4 w-4" /> Confirmar con incidencia</>}
              </Button>
              <Button onClick={() => handleReceive(false)} disabled={isSaving}>
                {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : <><CheckCircle className="mr-2 h-4 w-4" /> Confirmar recepción</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
